import type { CSSProperties, MouseEvent as ReactMouseEvent, RefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
    GroupBase,
    GroupProps,
    IndicatorsContainerProps,
    MenuListProps,
    MenuProps,
    OptionProps,
} from 'react-select';
import { components as SelectComponents } from 'react-select';
import { Logo } from '~/components/Company';
import CenteredInfoLayout from '~/components/layout/templates/CenteredInfo';
import { AvatarUser } from '~/components/new/Avatar';
import Chip from '~/components/new/Chip';
import IconAction from '~/components/new/IconAction';
import { MenuRowCustom } from '~/components/new/Menu';
import Select, { type SelectOption } from '~/components/ui/forms/Select';
import Icon, { RemixIconNames, type IconNames } from '~/components/ui/Icon';
import Modal from '~/components/ui/Modal';
import { classNames } from '~/utils/styles';
import type { QuickNavItem, QuickNavSection, QuickNavSectionId } from './types';

export type QuickNavSelectOption = SelectOption<string> & {
    path: string;
    icon: QuickNavItem['icon'];
    picture: QuickNavItem['picture'];
    admin?: boolean;
    customer: QuickNavItem['customer'];
    user: QuickNavItem['user'];
    sectionId: QuickNavSectionId;
};

export type QuickNavModalProps = {
    show: boolean;
    sections: QuickNavSection[];
    inputValue?: string;
    hideEmptyState?: boolean;
    onInputChange?: (value: string) => void;
    onSelect: (item: QuickNavItem, sectionId: QuickNavSectionId) => void;
    onClose: () => void;
};

function iconForState(icon: IconNames, active: boolean): IconNames {
    if (!active || typeof icon !== 'string' || !icon.endsWith('Line')) return icon;
    return icon.replace(/Line$/, 'Fill') as IconNames;
}

function ShortcutHint({ keys, label }: { keys: string; label: string }) {
    return (
        <span className='quick-nav-shortcut'>
            <Chip
                size='small'
                color='grey'
                variant='outlined'>
                {keys}
            </Chip>
            <span className='desktop-caption-1-regular text-core-secondary'>{label}</span>
        </span>
    );
}

function QuickNavMenu(
    props: MenuProps<QuickNavSelectOption, false, GroupBase<QuickNavSelectOption>>,
) {
    const { children, innerProps, innerRef } = props;
    return (
        <div
            ref={innerRef}
            {...innerProps}
            className='quick-nav-select-menu'>
            {children}
        </div>
    );
}

function QuickNavMenuList(
    props: MenuListProps<QuickNavSelectOption, false, GroupBase<QuickNavSelectOption>>,
) {
    const { children, innerProps, innerRef } = props;
    return (
        <div
            ref={innerRef}
            {...innerProps}
            className='quick-nav-select-menu-list'>
            {children}
        </div>
    );
}

function QuickNavGroup(
    props: GroupProps<QuickNavSelectOption, false, GroupBase<QuickNavSelectOption>>,
) {
    const { children, label } = props;
    return (
        <section className='quick-nav-group'>
            {label && <div className='quick-nav-group-heading'>{label}</div>}
            <div className='quick-nav-group-list'>{children}</div>
        </section>
    );
}

function QuickNavOption(
    props: OptionProps<QuickNavSelectOption, false, GroupBase<QuickNavSelectOption>>,
) {
    const { data, innerProps, isFocused } = props;
    const optionRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!isFocused || !optionRef.current) return;
        optionRef.current.scrollIntoView({ block: 'nearest' });
    }, [isFocused]);

    return (
        <SelectComponents.Option
            {...props}
            innerRef={(ref) => {
                optionRef.current = ref;
            }}
            innerProps={{
                ...innerProps,
                // @ts-expect-error - data-test-component is not typed on react-select
                ['data-test-component']: 'QuickNav/Option',
                ['data-test-name']: data.value,
            }}>
            <MenuRowCustom
                icon={data.icon && iconForState(data.icon, isFocused)}
                className={isFocused ? 'quick-nav-option-focused' : undefined}
                data-test-name={data.value}>
                <span className='menu-row-label flex items-center gap-3'>
                    {data.picture && (
                        <Logo
                            url={data.picture.url}
                            name={String(data.label)}
                            size={16}
                        />
                    )}
                    {data.user && (
                        <AvatarUser
                            user={data.user}
                            size='xsmall'
                        />
                    )}
                    <span className='menu-row-label-text'>{data.label}</span>
                </span>
                <span className='quick-nav-option-trailing'>
                    {data.admin && (
                        <span className='desktop-overline text-core-tertiary'>Admin</span>
                    )}
                    <Icon
                        icon={RemixIconNames.RiArrowRightSLine}
                        colorVar='core-tertiary'
                    />
                </span>
            </MenuRowCustom>
        </SelectComponents.Option>
    );
}

function QuickNavIndicatorsContainer(
    props: IndicatorsContainerProps<
        QuickNavSelectOption,
        false,
        GroupBase<QuickNavSelectOption>
    > & {
        showClear: boolean;
        onClearInput: () => void;
    },
) {
    const { children, showClear, onClearInput, ...rest } = props;

    const clear = (e: ReactMouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClearInput();
    };

    return (
        <SelectComponents.IndicatorsContainer {...rest}>
            {showClear && (
                <IconAction
                    className='quick-nav-clear'
                    icon={RemixIconNames.RiCloseCircleFill}
                    colorVar='core-tertiary'
                    title='Clear search'
                    data-test-component='QuickNav/Clear'
                    onMouseDown={clear}
                    onClick={clear}
                />
            )}
            {children}
        </SelectComponents.IndicatorsContainer>
    );
}

// react-select moves the highlight to the first row when the highlighted option object is replaced,
// so a section that did not change must keep its option objects when another section updates.
const selectGroups = new WeakMap<QuickNavSection, GroupBase<QuickNavSelectOption>>();

function toSelectGroup(section: QuickNavSection): GroupBase<QuickNavSelectOption> {
    const cached = selectGroups.get(section);
    if (cached) return cached;

    const group = {
        label: section.title,
        options: section.items.map((item) => ({
            value: item.id,
            label: item.label,
            path: item.path,
            icon: item.icon,
            picture: item.picture,
            admin: item.admin,
            customer: item.customer,
            user: item.user,
            sectionId: section.id,
        })),
    };
    selectGroups.set(section, group);
    return group;
}

function toSelectGroups(sections: QuickNavSection[]): GroupBase<QuickNavSelectOption>[] {
    return sections.filter((section) => section.items.length > 0).map(toSelectGroup);
}

const selectMenuStyles = {
    container: (base: CSSProperties) => ({
        ...base,
        width: '100%',
        maxWidth: '100%',
    }),
    control: (base: CSSProperties) => ({
        ...base,
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        height: 40,
        minHeight: 40,
        borderWidth: 0,
        borderStyle: 'none',
        borderColor: 'transparent',
        boxShadow: 'none',
        outline: 'none',
        backgroundColor: 'var(--surface-action-primary-light-enabled)',
        borderRadius: 20,
    }),
    menu: (base: CSSProperties) => ({
        ...base,
        position: 'static' as const,
        width: '100%',
        maxWidth: '100%',
        minWidth: '100%',
        boxShadow: 'none',
        border: 'none',
        margin: 0,
    }),
    menuPortal: (base: CSSProperties) => ({
        ...base,
        position: 'static' as const,
        top: 'auto',
        left: 'auto',
        width: '100%',
    }),
};

function useFocusSelectAndFirstOption({
    enabled,
    containerRef,
}: {
    enabled: boolean;
    containerRef: RefObject<HTMLElement | null>;
}) {
    useEffect(() => {
        if (!enabled) return;
        const input = containerRef.current?.querySelector<HTMLInputElement>('input');
        if (!input) return;

        input.focus({ preventScroll: true });

        requestAnimationFrame(() => {
            input.dispatchEvent(
                new KeyboardEvent('keydown', {
                    key: 'ArrowDown',
                    bubbles: true,
                    cancelable: true,
                }),
            );
        });
    }, [enabled, containerRef]);
}

export default function QuickNavModal({
    show,
    sections,
    inputValue,
    hideEmptyState = false,
    onInputChange,
    onSelect,
    onClose,
}: QuickNavModalProps) {
    const options = useMemo(() => toSelectGroups(sections), [sections]);
    const hasOptions = options.some((group) => group.options.length > 0);
    const trimmedQuery = inputValue?.trim() ?? '';
    const isEmpty = !hasOptions && trimmedQuery.length > 0 && !hideEmptyState;
    const [menuPortalEl, setMenuPortalEl] = useState<HTMLDivElement | null>(null);
    const selectWrapRef = useRef<HTMLDivElement>(null);

    useFocusSelectAndFirstOption({
        enabled: show && Boolean(menuPortalEl),
        containerRef: selectWrapRef,
    });

    return (
        <Modal
            show={show}
            usePortal
            size='small'
            className='quick-nav-modal'
            onClose={onClose}
            data-test-component='QuickNav/Modal'>
            <div
                className='quick-nav-modal-header'
                ref={selectWrapRef}
                data-test-name='header'>
                {menuPortalEl && (
                    <Select
                        classNamePrefix='search-select-input'
                        className='quick-nav-select'
                        options={options}
                        value={null}
                        inputValue={inputValue}
                        onInputChange={(value: string, meta: { action: string }) => {
                            if (meta.action === 'input-change' || meta.action === 'set-value') {
                                onInputChange?.(value);
                            }
                        }}
                        onChange={(option) => {
                            const selected = option as QuickNavSelectOption;
                            onSelect(
                                {
                                    id: selected.value,
                                    path: selected.path,
                                    label: String(selected.label),
                                    icon: selected.icon,
                                    picture: selected.picture,
                                    admin: selected.admin,
                                    customer: selected.customer,
                                    user: selected.user,
                                },
                                selected.sectionId,
                            );
                        }}
                        placeholder='Go to…'
                        menuIsOpen={!isEmpty}
                        captureMenuScroll={false}
                        maxMenuHeight={Number.MAX_SAFE_INTEGER}
                        menuPortalTarget={menuPortalEl}
                        filterOption={() => true}
                        growable={false}
                        controlShouldRenderValue={false}
                        noOptionsMessage={() => null}
                        components={{
                            Menu: QuickNavMenu,
                            MenuList: QuickNavMenuList,
                            Group: QuickNavGroup,
                            Option: QuickNavOption,
                            DropdownIndicator: () => null,
                            IndicatorsContainer: (
                                props: IndicatorsContainerProps<
                                    QuickNavSelectOption,
                                    false,
                                    GroupBase<QuickNavSelectOption>
                                >,
                            ) => (
                                <QuickNavIndicatorsContainer
                                    {...props}
                                    showClear={trimmedQuery.length > 0}
                                    onClearInput={() => onInputChange?.('')}
                                />
                            ),
                        }}
                        styles={selectMenuStyles}
                    />
                )}
            </div>
            <Modal.Body
                className={classNames({
                    'quick-nav-modal-body': true,
                    'quick-nav-modal-body--empty': isEmpty,
                })}
                data-test-name='body'>
                <div
                    id='quick-nav-menu-portal'
                    className='quick-nav-menu-portal'
                    ref={setMenuPortalEl}
                />
                {isEmpty && (
                    <section className='quick-nav-empty'>
                        <CenteredInfoLayout.Placeholder
                            icon='emptyGeneric'
                            title={`No results for “${trimmedQuery}”`}
                            subtitle='Try a shorter name or check the spelling.'
                        />
                    </section>
                )}
            </Modal.Body>
            <Modal.Footer
                align='left'
                data-test-name='footer'>
                <ShortcutHint
                    keys='↑↓'
                    label='Navigate'
                />
                <ShortcutHint
                    keys='↵'
                    label='Open'
                />
                <ShortcutHint
                    keys='esc'
                    label='Close'
                />
            </Modal.Footer>
        </Modal>
    );
}
